import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  Legend,
} from 'recharts';

const fmt = (val) =>
  '$' +
  Number(val).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const fmtNoCents = (val) =>
  '$' + Number(val).toLocaleString('en-US', { maximumFractionDigits: 0 });

// ─── Shared mock data ──────────────────────────────────────────────
const PL_MOCK = {
  cc_revenue: 7649.16,
  cash_revenue: 3936.00,
  sales_tax: 695.11,
  tax_rate: 6,
  net_revenue: 10890.05,
  expenses: {
    maintenance: 248.00,
    utilities: { Electric: 375.00, 'Water/Sewer': 188.50 },
    soap: 420.75,
    total: 1232.25,
  },
  net: 9657.80,
  total_revenue: 11585.16,
  ss_total: 4496.00,
  auto_total: 5629.10,
  vac_vending_total: 1460.06,
  period_days: 30,
};

const RECENT_CASH_OUTS_MOCK = [
  { date: 'Apr 14, 2026', total: 368.45, ss: 156.00, auto: 168.45, vac: 44.00, avg: 92.11 },
  { date: 'Apr 10, 2026', total: 420.75, ss: 179.10, auto: 197.45, vac: 44.20, avg: 140.25 },
  { date: 'Apr 7, 2026', total: 313.50, ss: 130.00, auto: 150.40, vac: 33.10, avg: 78.38 },
  { date: 'Apr 3, 2026', total: 357.80, ss: 148.40, auto: 168.00, vac: 41.40, avg: null },
];

// Matches WASH_UNITS in PLDashboard.js
const WASH_UNITS = ['SS Bay 1', 'SS Bay 2', 'SS Bay 3', 'SS Bay 4', 'Auto Wash'];
const WASH_COLORS = {
  'SS Bay 1': '#60a5fa',
  'SS Bay 2': '#3b82f6',
  'SS Bay 3': '#2563eb',
  'SS Bay 4': '#1d4ed8',
  'Auto Wash': '#10b981',
};

const PL_WASH_BAY_SERIES = [
  { month: 'Oct', 'SS Bay 1': 980,  'SS Bay 2': 1010, 'SS Bay 3': 960,  'SS Bay 4': 1000, 'Auto Wash': 4820 },
  { month: 'Nov', 'SS Bay 1': 1040, 'SS Bay 2': 1080, 'SS Bay 3': 1030, 'SS Bay 4': 1060, 'Auto Wash': 5120 },
  { month: 'Dec', 'SS Bay 1': 790,  'SS Bay 2': 810,  'SS Bay 3': 780,  'SS Bay 4': 800,  'Auto Wash': 3940 },
  { month: 'Jan', 'SS Bay 1': 760,  'SS Bay 2': 780,  'SS Bay 3': 750,  'SS Bay 4': 760,  'Auto Wash': 3880 },
  { month: 'Feb', 'SS Bay 1': 930,  'SS Bay 2': 950,  'SS Bay 3': 920,  'SS Bay 4': 960,  'Auto Wash': 4640 },
  { month: 'Mar', 'SS Bay 1': 1090, 'SS Bay 2': 1120, 'SS Bay 3': 1080, 'SS Bay 4': 1100, 'Auto Wash': 5320 },
  { month: 'Apr', 'SS Bay 1': 1120, 'SS Bay 2': 1140, 'SS Bay 3': 1110, 'SS Bay 4': 1126, 'Auto Wash': 5629 },
];

const PL_VAC_SERIES = [
  { month: 'Oct', 'Vacuum 1': 180, 'Vacuum 2': 165, 'Vacuum 3': 175, 'Vacuum 4': 170 },
  { month: 'Nov', 'Vacuum 1': 195, 'Vacuum 2': 180, 'Vacuum 3': 188, 'Vacuum 4': 184 },
  { month: 'Dec', 'Vacuum 1': 150, 'Vacuum 2': 142, 'Vacuum 3': 148, 'Vacuum 4': 144 },
  { month: 'Jan', 'Vacuum 1': 142, 'Vacuum 2': 138, 'Vacuum 3': 145, 'Vacuum 4': 140 },
  { month: 'Feb', 'Vacuum 1': 175, 'Vacuum 2': 168, 'Vacuum 3': 172, 'Vacuum 4': 170 },
  { month: 'Mar', 'Vacuum 1': 210, 'Vacuum 2': 200, 'Vacuum 3': 205, 'Vacuum 4': 202 },
  { month: 'Apr', 'Vacuum 1': 218, 'Vacuum 2': 208, 'Vacuum 3': 214, 'Vacuum 4': 212 },
];
const VAC_COLORS = ['#c084fc', '#a855f7', '#9333ea', '#7e22ce'];

// Chemical table data — columns match ChemicalDashboard.js (Chemical, Size, Cost, Avg Days/Jug, Last Changed, Days Remaining, Changes)
const CHEMICALS_SS = [
  { name: 'Presoak',    size: 5,  cost: 68.50,  avgDays: 32, lastChanged: 'Apr 6',  daysRemaining: 8,  changes: 14, lowStock: true  },
  { name: 'Soap',       size: 5,  cost: 74.25,  avgDays: 45, lastChanged: 'Mar 28', daysRemaining: 19, changes: 12, lowStock: false },
  { name: 'Tire Shine', size: 5,  cost: 82.00,  avgDays: 58, lastChanged: 'Mar 10', daysRemaining: 34, changes: 9,  lowStock: false },
  { name: 'Wax',        size: 5,  cost: 96.40,  avgDays: 62, lastChanged: 'Mar 4',  daysRemaining: 41, changes: 8,  lowStock: false },
];

const CHEMICALS_AUTO = [
  { name: 'Foam Bath',     size: 15, cost: 132.00, avgDays: 38, lastChanged: 'Apr 6',  daysRemaining: 11, changes: 11, lowStock: true  },
  { name: 'Rain-X',        size: 15, cost: 168.50, avgDays: 52, lastChanged: 'Mar 22', daysRemaining: 26, changes: 9,  lowStock: false },
  { name: 'Triple Foam',   size: 15, cost: 148.75, avgDays: 68, lastChanged: 'Mar 4',  daysRemaining: 44, changes: 7,  lowStock: false },
  { name: 'Drying Agent',  size: 15, cost: 154.00, avgDays: 74, lastChanged: 'Feb 24', daysRemaining: 52, changes: 6,  lowStock: false },
];

const LOW_STOCK = [
  { name: 'Presoak', loc: 'SS Bays', days: 8 },
  { name: 'Foam Bath', loc: 'Auto Wash', days: 11 },
];

const OPEN_WORK_ORDERS = [
  { id: 1, title: 'Replace bay 2 coin meter', type: 'Repair', equipment: 'SS Bay 2', priority: 'high', status: 'In Progress', due: 'Apr 18' },
  { id: 2, title: 'Clean vacuum filters', type: 'Task', equipment: 'Vacuums', priority: 'normal', status: 'Open', due: 'Apr 22' },
  { id: 3, title: 'Recalibrate CAT pump #1', type: 'Repair', equipment: 'Auto Wash', priority: 'urgent', status: 'Open', due: 'Apr 16' },
  { id: 4, title: 'Refill vending machine', type: 'Task', equipment: 'Vending', priority: 'low', status: 'Open', due: 'Apr 25' },
];

const OVERDUE_ITEMS = [
  { title: 'Monthly equipment inspection', equipment: 'Building', overdueBy: 3 },
];

const UPCOMING_SCHEDULED = [
  { title: 'Oil change — air compressor', equipment: 'Building', daysUntil: 2, dueDate: 'Apr 19' },
  { title: 'Replace IBA brush tips', equipment: 'Auto Wash', daysUntil: 6, dueDate: 'Apr 23' },
];

// ─── Shared UI helpers ─────────────────────────────────────────────
const BrowserFrame = ({ urlPath, children }) => (
  <div className="rounded-xl border border-gray-200 bg-white shadow-xl overflow-hidden">
    <div className="bg-gray-100 border-b border-gray-200 px-4 py-2 flex items-center gap-2">
      <span className="w-3 h-3 rounded-full bg-red-400"></span>
      <span className="w-3 h-3 rounded-full bg-yellow-400"></span>
      <span className="w-3 h-3 rounded-full bg-green-400"></span>
      <div className="flex-1 text-center text-xs text-gray-500 font-mono truncate">
        carwashcopilot.com{urlPath}
      </div>
    </div>
    <div className="bg-gray-50 p-4 sm:p-6">{children}</div>
  </div>
);

const priorityPill = (p) => {
  switch (p) {
    case 'urgent': return 'bg-red-100 text-red-800';
    case 'high':   return 'bg-orange-100 text-orange-800';
    case 'normal': return 'bg-blue-100 text-blue-800';
    case 'low':    return 'bg-gray-100 text-gray-700';
    default:       return 'bg-gray-100 text-gray-700';
  }
};

const priorityDot = (p) => {
  switch (p) {
    case 'urgent': return 'bg-red-500';
    case 'high':   return 'bg-orange-500';
    case 'normal': return 'bg-blue-400';
    case 'low':    return 'bg-gray-400';
    default:       return 'bg-gray-400';
  }
};

// ─── Dashboard mock ────────────────────────────────────────────────
const DashboardMock = () => (
  <div className="space-y-6">
    <h3 className="text-2xl sm:text-3xl font-bold text-gray-900">Dashboard</h3>

    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
      <div className="lg:col-span-2 bg-white rounded-lg shadow overflow-hidden">
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <h4 className="text-base sm:text-lg font-semibold text-gray-900">P&amp;L Summary</h4>
            <span className="text-blue-600 text-xs sm:text-sm font-medium">Full Report</span>
          </div>
          <div className="hidden sm:flex items-center gap-2 text-xs text-gray-500">
            <span className="border border-gray-300 rounded-md px-2 py-1 bg-white">Mar 16</span>
            <span>to</span>
            <span className="border border-gray-300 rounded-md px-2 py-1 bg-white">Apr 15</span>
          </div>
        </div>
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 sm:px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase">Item</th>
              <th className="px-4 sm:px-6 py-2 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            <tr>
              <td className="px-4 sm:px-6 py-2 text-gray-900 pl-8 sm:pl-10">Credit Card Revenue</td>
              <td className="px-4 sm:px-6 py-2 text-right text-gray-900">{fmt(PL_MOCK.cc_revenue)}</td>
            </tr>
            <tr>
              <td className="px-4 sm:px-6 py-2 text-gray-900 pl-8 sm:pl-10">Cash Revenue</td>
              <td className="px-4 sm:px-6 py-2 text-right text-gray-900">{fmt(PL_MOCK.cash_revenue)}</td>
            </tr>
            <tr className="bg-gray-50/50">
              <td className="px-4 sm:px-6 py-2 font-medium text-gray-900">Total Revenue</td>
              <td className="px-4 sm:px-6 py-2 text-right font-medium text-gray-900">{fmt(PL_MOCK.total_revenue)}</td>
            </tr>
            <tr>
              <td className="px-4 sm:px-6 py-2 text-red-700 pl-8 sm:pl-10">Sales Tax ({PL_MOCK.tax_rate}%)</td>
              <td className="px-4 sm:px-6 py-2 text-right text-red-700">({fmt(PL_MOCK.sales_tax)})</td>
            </tr>
            <tr className="bg-gray-50/50">
              <td className="px-4 sm:px-6 py-2 font-bold text-gray-900">Net Revenue</td>
              <td className="px-4 sm:px-6 py-2 text-right font-bold text-gray-900">{fmt(PL_MOCK.net_revenue)}</td>
            </tr>
            <tr>
              <td className="px-4 sm:px-6 py-2 text-red-700 pl-8 sm:pl-10">Maintenance</td>
              <td className="px-4 sm:px-6 py-2 text-right text-red-700">({fmt(PL_MOCK.expenses.maintenance)})</td>
            </tr>
            {Object.entries(PL_MOCK.expenses.utilities).map(([cat, amt]) => (
              <tr key={cat}>
                <td className="px-4 sm:px-6 py-2 text-red-700 pl-8 sm:pl-10">{cat}</td>
                <td className="px-4 sm:px-6 py-2 text-right text-red-700">({fmt(amt)})</td>
              </tr>
            ))}
            <tr>
              <td className="px-4 sm:px-6 py-2 text-red-700 pl-8 sm:pl-10">Soap/Chemicals (est.)</td>
              <td className="px-4 sm:px-6 py-2 text-right text-red-700">({fmt(PL_MOCK.expenses.soap)})</td>
            </tr>
            <tr className="bg-gray-50/50">
              <td className="px-4 sm:px-6 py-2 font-medium text-red-700">Total Expenses</td>
              <td className="px-4 sm:px-6 py-2 text-right font-medium text-red-700">({fmt(PL_MOCK.expenses.total)})</td>
            </tr>
            <tr className="bg-gray-100 font-bold">
              <td className="px-4 sm:px-6 py-2 text-gray-900">Net Income</td>
              <td className="px-4 sm:px-6 py-2 text-right text-green-700">{fmt(PL_MOCK.net)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="flex flex-col gap-3 sm:gap-4">
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm font-medium text-gray-600">Total Revenue</div>
          <div className="text-xl sm:text-2xl font-bold text-gray-900 mt-1">{fmt(PL_MOCK.total_revenue)}</div>
          <div className="text-xs text-gray-500 mt-1">{fmt(PL_MOCK.total_revenue / PL_MOCK.period_days)}/day avg</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm font-medium text-gray-600">Self-Service Bays</div>
          <div className="text-xl sm:text-2xl font-bold text-blue-600 mt-1">{fmt(PL_MOCK.ss_total)}</div>
          <div className="text-xs text-gray-500 mt-1">{((PL_MOCK.ss_total / PL_MOCK.total_revenue) * 100).toFixed(1)}% of total</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm font-medium text-gray-600">Auto Wash</div>
          <div className="text-xl sm:text-2xl font-bold text-green-600 mt-1">{fmt(PL_MOCK.auto_total)}</div>
          <div className="text-xs text-gray-500 mt-1">{((PL_MOCK.auto_total / PL_MOCK.total_revenue) * 100).toFixed(1)}% of total</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm font-medium text-gray-600">Vacuums &amp; Vending</div>
          <div className="text-xl sm:text-2xl font-bold text-purple-600 mt-1">{fmt(PL_MOCK.vac_vending_total)}</div>
          <div className="text-xs text-gray-500 mt-1">{((PL_MOCK.vac_vending_total / PL_MOCK.total_revenue) * 100).toFixed(1)}% of total</div>
        </div>
        <div className="bg-green-50 p-4 rounded-lg shadow">
          <div className="text-sm font-medium text-gray-600">Net Profit</div>
          <div className="text-xl sm:text-2xl font-bold text-green-700 mt-1">{fmt(PL_MOCK.net)}</div>
          <div className="text-xs text-gray-500 mt-1">After {fmt(PL_MOCK.sales_tax)} sales tax</div>
          <div className="text-xs text-gray-500 mt-1">{((PL_MOCK.net / PL_MOCK.net_revenue) * 100).toFixed(1)}% margin</div>
        </div>
      </div>
    </div>

    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 flex justify-between items-center">
        <h4 className="text-base sm:text-lg font-semibold text-gray-900">Recent Cash Outs</h4>
        <span className="text-blue-600 text-xs sm:text-sm font-medium">View All</span>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
              <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
              <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase">Self-Service</th>
              <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase">Auto Wash</th>
              <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase">Vacuums</th>
              <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase">Daily Avg</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {RECENT_CASH_OUTS_MOCK.map((r) => (
              <tr key={r.date}>
                <td className="px-3 sm:px-6 py-2 whitespace-nowrap font-medium text-gray-900">{r.date}</td>
                <td className="px-3 sm:px-6 py-2 whitespace-nowrap font-semibold text-gray-900">{fmt(r.total)}</td>
                <td className="px-3 sm:px-6 py-2 whitespace-nowrap text-gray-900">{fmt(r.ss)}</td>
                <td className="px-3 sm:px-6 py-2 whitespace-nowrap text-gray-900">{fmt(r.auto)}</td>
                <td className="px-3 sm:px-6 py-2 whitespace-nowrap text-gray-900">{fmt(r.vac)}</td>
                <td className="px-3 sm:px-6 py-2 whitespace-nowrap font-medium text-gray-900">{r.avg == null ? 'N/A' : fmt(r.avg)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);

// ─── P&L mock ──────────────────────────────────────────────────────
const PLMock = () => {
  const totalUtilities = Object.values(PL_MOCK.expenses.utilities).reduce((a, b) => a + b, 0);
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-2xl sm:text-3xl font-bold text-gray-900">Profit &amp; Loss</h3>
        <div className="flex gap-2 flex-wrap">
          <span className="text-xs sm:text-sm px-3 py-1.5 border border-gray-300 rounded-lg text-gray-700 bg-white">View All Cash Outs</span>
          <span className="text-xs sm:text-sm px-3 py-1.5 border border-gray-300 rounded-lg text-gray-700 bg-white">Upload CC Transactions</span>
          <span className="text-xs sm:text-sm px-3 py-1.5 border border-gray-300 rounded-lg text-gray-700 bg-white">Upload Bill</span>
          <span className="text-xs sm:text-sm px-3 py-1.5 bg-blue-600 text-white rounded-lg font-medium">New Cash Out</span>
        </div>
      </div>

      {/* Date Range filter */}
      <div className="bg-white rounded-lg shadow p-3 sm:p-4 flex items-center gap-2 flex-wrap">
        <span className="text-sm font-medium text-gray-700 mr-1">Date Range</span>
        {['30 days', '3 months', '6 months', '1 year', 'All time', 'Custom'].map((p, i) => (
          <span
            key={p}
            className={`px-2.5 py-1 rounded-md text-xs font-medium ${
              i === 0 ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'
            }`}
          >
            {p}
          </span>
        ))}
        <span className="sm:ml-auto text-xs sm:text-sm px-3 py-1.5 border border-gray-300 rounded-lg text-gray-700 bg-white">
          Export Excel
        </span>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 sm:gap-4">
        <div className="bg-white rounded-lg shadow p-4 sm:p-5">
          <p className="text-xs sm:text-sm text-gray-500">Total Revenue</p>
          <p className="text-lg sm:text-2xl font-bold text-gray-900 mt-1">{fmt(PL_MOCK.total_revenue)}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4 sm:p-5">
          <p className="text-xs sm:text-sm text-gray-500">CC Revenue</p>
          <p className="text-lg sm:text-2xl font-bold text-blue-600 mt-1">{fmt(PL_MOCK.cc_revenue)}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4 sm:p-5">
          <p className="text-xs sm:text-sm text-gray-500">Cash Revenue</p>
          <p className="text-lg sm:text-2xl font-bold text-green-600 mt-1">{fmt(PL_MOCK.cash_revenue)}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4 sm:p-5">
          <p className="text-xs sm:text-sm text-gray-500">Total Expenses</p>
          <p className="text-lg sm:text-2xl font-bold text-red-600 mt-1">{fmt(PL_MOCK.expenses.total)}</p>
          <div className="mt-1 text-xs text-gray-500 space-y-0.5">
            <p>Maintenance: {fmt(PL_MOCK.expenses.maintenance)}</p>
            <p>Utilities: {fmt(totalUtilities)}</p>
            <p>Soap/Chemicals: {fmt(PL_MOCK.expenses.soap)}</p>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 sm:p-5 col-span-2 md:col-span-1">
          <p className="text-xs sm:text-sm text-gray-500">Net P&amp;L</p>
          <p className="text-lg sm:text-2xl font-bold text-green-600 mt-1">{fmt(PL_MOCK.net)}</p>
          <p className="text-xs text-gray-500 mt-1">After {fmt(PL_MOCK.sales_tax)} sales tax</p>
        </div>
      </div>

      {/* Granularity toggle */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-medium text-gray-700 mr-1">View by:</span>
        {['Daily', 'Weekly', 'Monthly', 'Yearly'].map((g) => (
          <span
            key={g}
            className={`px-3 py-1 rounded-md text-xs sm:text-sm font-medium ${
              g === 'Monthly' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'
            }`}
          >
            {g}
          </span>
        ))}
      </div>

      {/* Wash Bay Revenue chart */}
      <div className="bg-white rounded-lg shadow p-4 sm:p-6">
        <h4 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Wash Bay Revenue Over Time</h4>
        <div style={{ width: '100%', height: 260 }}>
          <ResponsiveContainer>
            <BarChart data={PL_WASH_BAY_SERIES} margin={{ top: 5, right: 8, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} />
              <YAxis tickFormatter={(v) => `$${v}`} tick={{ fontSize: 12 }} width={55} />
              <Tooltip formatter={(v) => fmt(v)} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {WASH_UNITS.map((u) => (
                <Bar key={u} dataKey={u} stackId="wash" fill={WASH_COLORS[u]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Vacuum Revenue chart */}
      <div className="bg-white rounded-lg shadow p-4 sm:p-6">
        <h4 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Vacuum Revenue Over Time</h4>
        <div style={{ width: '100%', height: 240 }}>
          <ResponsiveContainer>
            <BarChart data={PL_VAC_SERIES} margin={{ top: 5, right: 8, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} />
              <YAxis tickFormatter={(v) => `$${v}`} tick={{ fontSize: 12 }} width={45} />
              <Tooltip formatter={(v) => fmt(v)} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {['Vacuum 1', 'Vacuum 2', 'Vacuum 3', 'Vacuum 4'].map((v, i) => (
                <Bar key={v} dataKey={v} stackId="vac" fill={VAC_COLORS[i]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

// ─── Inventory (Soap Tracking) mock ────────────────────────────────
const ChemicalTable = ({ title, subtitle, items }) => (
  <div className="bg-white rounded-lg shadow overflow-hidden">
    <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
      <h4 className="text-base sm:text-xl font-semibold text-gray-900">{title}</h4>
      {subtitle && <p className="text-xs sm:text-sm text-gray-500">{subtitle}</p>}
    </div>
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase">Chemical</th>
            <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase">Size</th>
            <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase">Cost</th>
            <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase">Avg Days/Jug</th>
            <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase">Last Changed</th>
            <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase">Days Remaining</th>
            <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase">Changes</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {items.map((c) => {
            const daysColor = c.daysRemaining <= 14 ? 'text-yellow-600 font-medium' : 'text-green-600';
            return (
              <tr key={c.name} className={c.lowStock ? 'bg-yellow-50' : ''}>
                <td className="px-3 sm:px-6 py-3 whitespace-nowrap font-medium text-gray-900">
                  <div className="flex items-center gap-2">
                    {c.name}
                    {c.lowStock && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                        Low
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-3 sm:px-6 py-3 whitespace-nowrap text-gray-600">{c.size} gal</td>
                <td className="px-3 sm:px-6 py-3 whitespace-nowrap text-gray-900">${c.cost.toFixed(2)}</td>
                <td className="px-3 sm:px-6 py-3 whitespace-nowrap text-gray-900">{c.avgDays}</td>
                <td className="px-3 sm:px-6 py-3 whitespace-nowrap text-gray-900">{c.lastChanged}</td>
                <td className={`px-3 sm:px-6 py-3 whitespace-nowrap ${daysColor}`}>{c.daysRemaining}</td>
                <td className="px-3 sm:px-6 py-3 whitespace-nowrap text-gray-600">{c.changes}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </div>
);

const InventoryMock = () => (
  <div className="space-y-6">
    <div className="flex items-center justify-between flex-wrap gap-2">
      <h3 className="text-2xl sm:text-3xl font-bold text-gray-900">Soap Tracking</h3>
      <div className="flex gap-2 flex-wrap">
        <span className="text-xs sm:text-sm px-3 py-1.5 border border-gray-300 rounded-lg text-gray-700 bg-white">Refresh Prices</span>
        <span className="text-xs sm:text-sm px-3 py-1.5 bg-green-600 text-white rounded-lg font-medium">Log Soap Change</span>
        <span className="text-xs sm:text-sm px-3 py-1.5 bg-blue-600 text-white rounded-lg font-medium">+ Add Chemical</span>
      </div>
    </div>

    {/* Low Stock Alerts */}
    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
      <h4 className="text-base font-semibold text-yellow-800 mb-2">Low Stock Alerts</h4>
      <div className="space-y-1">
        {LOW_STOCK.map((a) => (
          <div key={a.name} className="text-sm text-yellow-700">
            <span className="font-medium">{a.name}</span>
            <span className="text-yellow-500 ml-1">({a.loc})</span>
            {' '}&mdash; ~{a.days} days of supply remaining
          </div>
        ))}
      </div>
    </div>

    {/* Sort controls */}
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-sm font-medium text-gray-700 mr-1">Sort by:</span>
      {['Custom Order', 'Days Remaining', 'Name'].map((s, i) => (
        <span
          key={s}
          className={`px-3 py-1 rounded-lg text-xs sm:text-sm font-medium ${
            i === 0 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'
          }`}
        >
          {s}
        </span>
      ))}
    </div>

    <ChemicalTable title="SS Bays" subtitle="All 4 bays share these chemicals" items={CHEMICALS_SS} />
    <ChemicalTable title="Auto Wash" subtitle="WashWorld chemicals" items={CHEMICALS_AUTO} />
  </div>
);

// ─── Maintenance mock ──────────────────────────────────────────────
const MaintenanceMock = () => (
  <div className="space-y-6">
    <div className="flex items-center justify-between flex-wrap gap-2">
      <h3 className="text-2xl sm:text-3xl font-bold text-gray-900">Maintenance</h3>
      <div className="flex gap-2 flex-wrap">
        <span className="text-xs sm:text-sm px-3 py-1.5 border border-gray-300 rounded-lg text-gray-700 bg-white">Schedules</span>
        <span className="text-xs sm:text-sm px-3 py-1.5 bg-red-600 text-white rounded-lg font-medium">+ New Repair</span>
        <span className="text-xs sm:text-sm px-3 py-1.5 bg-blue-600 text-white rounded-lg font-medium">+ New Task</span>
      </div>
    </div>

    {/* Overdue Alerts */}
    <div className="bg-red-50 border border-red-300 rounded-lg p-4">
      <h4 className="text-base font-semibold text-red-800 mb-2">Overdue Items</h4>
      <div className="space-y-1">
        {OVERDUE_ITEMS.map((i) => (
          <div key={i.title} className="text-sm text-red-700">
            <span className="font-medium">{i.title}</span>
            <span className="text-red-500 ml-1">({i.equipment})</span>
            {' '}&mdash; scheduled maintenance overdue by {i.overdueBy} days
          </div>
        ))}
      </div>
    </div>

    {/* Upcoming */}
    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
      <h4 className="text-base font-semibold text-yellow-800 mb-2">Upcoming Scheduled Maintenance (7 days)</h4>
      <div className="space-y-1">
        {UPCOMING_SCHEDULED.map((i) => (
          <div key={i.title} className="text-sm text-yellow-700">
            <span className="font-medium">{i.title}</span>
            <span className="text-yellow-500 ml-1">({i.equipment})</span>
            {' '}&mdash; due in {i.daysUntil} day{i.daysUntil !== 1 ? 's' : ''} ({i.dueDate})
          </div>
        ))}
      </div>
    </div>

    {/* Open Work Orders */}
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
        <h4 className="text-base sm:text-lg font-semibold text-gray-900">
          Open Tasks &amp; Repairs ({OPEN_WORK_ORDERS.length})
        </h4>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="w-6 sm:w-8 px-2 py-2"></th>
              <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
              <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
              <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase">Equipment</th>
              <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase">Priority</th>
              <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase">Due</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {OPEN_WORK_ORDERS.map((wo) => (
              <tr key={wo.id}>
                <td className="px-2 py-2">
                  <span className={`inline-block w-2.5 h-2.5 rounded-full ${priorityDot(wo.priority)}`} />
                </td>
                <td className="px-3 sm:px-6 py-2 whitespace-nowrap font-medium text-gray-900">{wo.title}</td>
                <td className="px-3 sm:px-6 py-2 whitespace-nowrap text-gray-600">{wo.type}</td>
                <td className="px-3 sm:px-6 py-2 whitespace-nowrap text-gray-600">{wo.equipment}</td>
                <td className="px-3 sm:px-6 py-2 whitespace-nowrap">
                  <span className={`text-xs font-medium px-2 py-0.5 rounded ${priorityPill(wo.priority)}`}>
                    {wo.priority}
                  </span>
                </td>
                <td className="px-3 sm:px-6 py-2 whitespace-nowrap text-gray-600">{wo.status}</td>
                <td className="px-3 sm:px-6 py-2 whitespace-nowrap text-gray-900">{wo.due}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);

// ─── Feature grid content ──────────────────────────────────────────
const FEATURES = [
  {
    title: 'P&L Dashboard',
    body:
      'See monthly and daily revenue, expenses, and margin in one place. ' +
      'Auto-reconcile credit card batches against cash-outs, track utilities, ' +
      'and catch anomalies before they cost you.',
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.657-1.343 3-3 3s-3-1.343-3-3 1.343-3 3-3 3 1.343 3 3zm12-3c0 1.657-1.343 3-3 3s-3-1.343-3-3 1.343-3 3-3 3 1.343 3 3zM9 10l12-3" />
    ),
  },
  {
    title: 'Cash-Out Tracking',
    body:
      'Log bill changers, vacuums, vending, and auto-wash revenue with per-section ' +
      'inclusion. Detect changer shortfalls, track coin-in vs. bills-out, and ' +
      'keep attendants honest.',
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
    ),
  },
  {
    title: 'Maintenance & Work Orders',
    body:
      'Schedule recurring service, assign work orders to team members via SMS, ' +
      'log customer issues, and track maintenance spend by equipment area.',
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    ),
  },
  {
    title: 'Chemical Inventory',
    body:
      'Track soap, wax, and presoak levels. Log refills, watch consumption trends, ' +
      'and get reorder alerts before you run dry during a Saturday rush.',
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 9H9L8 4z" />
    ),
  },
  {
    title: 'Ask-AI Copilot',
    body:
      'Ask plain-English questions about your business — "how did tunnel revenue ' +
      'do in March?" — and get charts and answers grounded in your real data, ' +
      'not hallucinations.',
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
    ),
  },
  {
    title: 'Built for Multi-Location',
    body:
      'Run one site today, ten tomorrow. Switch locations with one click, invite ' +
      'site managers with scoped permissions, and roll up data across the whole ' +
      'operation.',
    icon: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    ),
  },
];

const FeatureIcon = ({ children }) => (
  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600">
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      {children}
    </svg>
  </div>
);

// ─── Tab switcher for screenshots ──────────────────────────────────
const PAGE_TABS = [
  { key: 'dashboard', label: 'Dashboard', path: '/', mock: DashboardMock },
  { key: 'pl', label: 'P&L', path: '/pl', mock: PLMock },
  { key: 'inventory', label: 'Inventory', path: '/inventory', mock: InventoryMock },
  { key: 'maintenance', label: 'Maintenance', path: '/maintenance', mock: MaintenanceMock },
];

const ScreenshotTabs = () => {
  const [active, setActive] = useState('dashboard');
  const current = PAGE_TABS.find((t) => t.key === active) || PAGE_TABS[0];
  const Mock = current.mock;

  return (
    <div>
      {/* Tab buttons */}
      <div className="flex flex-wrap justify-center gap-2 mb-4 sm:mb-6">
        {PAGE_TABS.map((t) => {
          const isActive = t.key === active;
          return (
            <button
              key={t.key}
              onClick={() => setActive(t.key)}
              className={`px-3 sm:px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      <BrowserFrame urlPath={current.path}>
        <Mock />
      </BrowserFrame>

      <p className="mt-3 text-center text-xs text-gray-500">
        A preview of the {current.label} page with mock data. Your real numbers replace everything shown here.
      </p>
    </div>
  );
};

// ─── Main landing page ────────────────────────────────────────────
const LandingPage = () => {
  return (
    <div className="space-y-16 sm:space-y-24">
      {/* Hero */}
      <section className="text-center pt-4 sm:pt-10">
        <p className="text-xs sm:text-sm font-semibold text-blue-600 uppercase tracking-wider mb-3">
          Car Wash Copilot
        </p>
        <h1 className="text-3xl sm:text-5xl font-bold text-gray-900 leading-tight">
          Run your car wash smarter.
        </h1>
        <p className="mt-4 sm:mt-6 text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
          Track revenue, chemicals, and maintenance in one place. Built for
          self-serve, in-bay automatic, and tunnel operators who want a clearer
          picture of where every dollar goes &mdash; and why.
        </p>
        <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          <Link
            to="/login"
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium inline-flex items-center justify-center"
          >
            Start free trial &rarr;
          </Link>
          <Link
            to="/contact"
            className="bg-white border border-gray-300 hover:bg-gray-50 text-gray-800 px-6 py-3 rounded-lg font-medium inline-flex items-center justify-center"
          >
            Contact us
          </Link>
        </div>
        <p className="mt-4 text-xs text-gray-500">
          14-day free trial &middot; No credit card required &middot; Set up in 10 minutes
        </p>
      </section>

      {/* Tabbed screenshots */}
      <section>
        <div className="text-center mb-6">
          <p className="text-xs font-semibold text-blue-600 uppercase tracking-wider mb-2">
            What you'll see
          </p>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
            Your whole operation on one screen.
          </h2>
          <p className="mt-3 text-sm text-gray-600 max-w-2xl mx-auto">
            Every section of your operation, one tab away. Numbers shown are
            illustrative &mdash; your real data replaces them after setup.
          </p>
        </div>
        <ScreenshotTabs />
      </section>

      {/* Feature grid */}
      <section>
        <div className="text-center mb-10">
          <p className="text-xs font-semibold text-blue-600 uppercase tracking-wider mb-2">
            What it does
          </p>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
            Everything a wash operator actually needs.
          </h2>
          <p className="mt-3 text-sm text-gray-600 max-w-2xl mx-auto">
            Not another generic SMB platform. Purpose-built for self-serve,
            IBA, and tunnel operations.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {FEATURES.map((f) => (
            <div
              key={f.title}
              className="bg-white rounded-lg border border-gray-200 p-5 space-y-3 hover:shadow-md transition-shadow"
            >
              <FeatureIcon>{f.icon}</FeatureIcon>
              <h3 className="text-base font-semibold text-gray-900">{f.title}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Trust section */}
      <section className="bg-white border border-gray-200 rounded-xl p-6 sm:p-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <h3 className="text-base font-semibold text-gray-900 mb-2">Your data is yours.</h3>
            <p className="text-sm text-gray-600">
              We never sell or share customer information. Each business account is
              fully isolated, and we only use data to run the features you've asked for.{' '}
              <Link to="/security" className="text-blue-600 hover:text-blue-800">
                See our security &amp; privacy commitments &rarr;
              </Link>
            </p>
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900 mb-2">Enterprise-grade infrastructure.</h3>
            <p className="text-sm text-gray-600">
              Hosted on Google Cloud with encrypted-at-rest Cloud SQL, automated
              backups, and TLS everywhere. Authentication by Firebase (SOC 2 / ISO 27001).{' '}
              <Link to="/security" className="text-blue-600 hover:text-blue-800">
                Security details &rarr;
              </Link>
            </p>
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900 mb-2">Transparent SMS.</h3>
            <p className="text-sm text-gray-600">
              Team SMS is strictly transactional &mdash; invites and work-order
              assignments only. Every message has STOP/HELP instructions.{' '}
              <Link to="/messaging-consent" className="text-blue-600 hover:text-blue-800">
                Messaging terms &rarr;
              </Link>
            </p>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="text-center bg-gradient-to-br from-blue-600 to-blue-700 text-white rounded-xl p-8 sm:p-12">
        <h2 className="text-2xl sm:text-3xl font-bold">Ready to see your numbers?</h2>
        <p className="mt-3 text-blue-100 max-w-lg mx-auto text-sm sm:text-base">
          Start a free 14-day trial. No credit card required. Setup takes about
          ten minutes.
        </p>
        <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
          <Link
            to="/login"
            className="bg-white text-blue-700 hover:bg-blue-50 px-6 py-3 rounded-lg font-medium"
          >
            Sign up free &rarr;
          </Link>
          <Link
            to="/contact"
            className="bg-blue-500 hover:bg-blue-400 text-white px-6 py-3 rounded-lg font-medium border border-blue-400"
          >
            Talk to us
          </Link>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;
